import { z } from 'zod';

// Typed site configuration owned by the gateway (ADR-0004). The gateway renders it to evcc.yaml;
// the installer UI edits it.
// Devices either reference an evcc template (type: template) or carry a raw evcc config
// (imported or expert configurations, e.g. custom plugins).

export const DeviceName = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,31}$/, 'nur Kleinbuchstaben, Ziffern und _ (max. 32 Zeichen)');

const ParamValue = z.union([z.string().max(1024), z.number(), z.boolean()]);

export const TemplateDevice = z.object({
  name: DeviceName,
  title: z.string().max(64).optional(),
  template: z.string().regex(/^[a-z0-9._-]+$/),
  params: z.record(z.string().regex(/^[a-zA-Z0-9_]+$/), ParamValue).default({}),
});
export type TemplateDevice = z.infer<typeof TemplateDevice>;

export const RawDevice = z.object({
  name: DeviceName,
  title: z.string().max(64).optional(),
  /** complete evcc device config without name (e.g. type: custom ...) */
  raw: z.record(z.string(), z.unknown()),
});
export type RawDevice = z.infer<typeof RawDevice>;

export const Device = z.union([TemplateDevice, RawDevice]);
export type Device = z.infer<typeof Device>;

export const MeterRole = z.enum(['grid', 'pv', 'battery', 'aux', 'ext', 'charge']);
export type MeterRole = z.infer<typeof MeterRole>;

export const Meter = z.intersection(Device, z.object({ role: MeterRole }));
export type Meter = z.infer<typeof Meter>;

export const ChargerKind = z.enum(['wallbox', 'heatpump']);

/** a switch output given by an evcc charger template (switch socket, relay) */
export const SwitchRef = TemplateDevice.omit({ name: true, title: true });
export type SwitchRef = z.infer<typeof SwitchRef>;

/**
 * SG-Ready over two switch outputs (ADR-0019), rendered as evcc type sgready-relay: boost closes
 * contact 2 (raised operation), dim contact 1 (blocked operation, optional)
 */
export const SgReadyRelayDevice = z.object({
  name: DeviceName,
  title: z.string().max(64).optional(),
  sgready: z.object({ boost: SwitchRef, dim: SwitchRef.optional() }),
});
export type SgReadyRelayDevice = z.infer<typeof SgReadyRelayDevice>;

export const ChargerDevice = z.union([TemplateDevice, SgReadyRelayDevice, RawDevice]);
export type ChargerDevice = z.infer<typeof ChargerDevice>;

export const Charger = z.intersection(ChargerDevice, z.object({ kind: ChargerKind.default('wallbox') }));
export type Charger = z.infer<typeof Charger>;

/**
 * Heat pump or heating rod (ADR-0019): evcc works with current and phases, the gateway converts
 * the power and sets everything through the API after activation.
 */
export const HeatingSettings = z
  .object({
    /** electrical power at full load, W */
    maxPower: z.number().int().min(100).max(50_000),
    /** lowest power of continuously controlled devices (heating rods); default maxPower */
    minPower: z.number().int().min(100).max(50_000).optional(),
    phases: z.union([z.literal(1), z.literal(3)]).default(1),
    /** PV mode: start from this feed-in, W; undefined: when the surplus covers the power */
    enableSurplus: z.number().int().min(0).max(50_000).optional(),
    /** PV mode: stop above this grid import, W (only with enableSurplus) */
    disableImport: z.number().int().min(0).max(50_000).optional(),
    /** minutes; undefined: evcc default (1 and 3 minutes) */
    enableDelay: z.number().int().min(0).max(60).optional(),
    disableDelay: z.number().int().min(0).max(60).optional(),
  })
  .refine((h) => h.minPower === undefined || h.minPower <= h.maxPower, {
    message: 'Die Mindestleistung ist größer als die Leistung',
    path: ['minPower'],
  });
export type HeatingSettings = z.infer<typeof HeatingSettings>;

export const Loadpoint = z.object({
  title: z.string().min(1).max(40),
  charger: DeviceName,
  meter: DeviceName.optional(),
  vehicle: DeviceName.optional(),
  mode: z.enum(['off', 'now', 'smart']).default('smart'),
  circuit: z.string().optional(),
  priority: z.number().int().min(0).max(10).optional(),
  // runtime settings: ignored by evcc in YAML, applied via API after activation (ADR-0004)
  minCurrent: z.number().min(0).max(63).optional(),
  maxCurrent: z.number().min(0).max(63).optional(),
  phases: z.union([z.literal(0), z.literal(1), z.literal(3)]).optional(),
  /** heat pumps and heating rods (ADR-0019), applied via API like the limits above */
  heating: HeatingSettings.optional(),
});
export type Loadpoint = z.infer<typeof Loadpoint>;

/**
 * House connection (ADR-0017): rendered as the root circuit of evcc. With a §14a device the
 * circuit has no meter, so evcc counts only wallboxes and heat pumps (the §14a limit must not
 * include the household); without one it measures at the grid meter.
 */
export const HouseConnection = z.object({
  /** circuit name in evcc.yaml, referenced by the loadpoints */
  name: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,32}$/)
    .default('hausanschluss'),
  title: z.string().max(64).optional(),
  /** fuse of the house connection in A per phase */
  maxCurrent: z.number().min(6).max(630).optional(),
});
export type HouseConnection = z.infer<typeof HouseConnection>;

// ---- tariffs (ADR-0018) ---------------------------------------------------------------------

/** EUR per kWh, as evcc expects it (the UI shows ct/kWh) */
const PricePerKWh = z.number().min(0).max(2);

export const WEEKDAYS = ['mo', 'di', 'mi', 'do', 'fr', 'sa', 'so'] as const;
export const Weekday = z.enum(WEEKDAYS);
export type Weekday = z.infer<typeof Weekday>;

const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Uhrzeit als HH:MM');

/** a time window with its own price, e.g. a night rate or §14a module 3 (time-variable grid fees) */
export const PriceZone = z
  .object({
    price: PricePerKWh,
    /** HH:MM, local time; from after to runs over midnight */
    from: ClockTime,
    to: ClockTime,
    /** empty: every day */
    days: z.array(Weekday).max(7).default([]),
  })
  .refine((zone) => zone.from !== zone.to, { message: 'Beginn und Ende sind gleich', path: ['to'] });
export type PriceZone = z.infer<typeof PriceZone>;

export const FixedGridTariff = z.object({
  kind: z.literal('fixed'),
  price: PricePerKWh,
  zones: z.array(PriceZone).max(8).default([]),
});

/**
 * dynamic tariff from an evcc tariff template (tibber, awattar, energy-charts-api, …); charges
 * (EUR/kWh) and tax (0.19 = 19 %) are template parameters when the template has them
 */
export const DynamicGridTariff = z.object({
  kind: z.literal('dynamic'),
  template: z.string().regex(/^[a-z0-9._-]+$/),
  params: z.record(z.string().regex(/^[a-zA-Z0-9_]+$/), ParamValue).default({}),
});

export const GridTariff = z.discriminatedUnion('kind', [FixedGridTariff, DynamicGridTariff]);
export type GridTariff = z.infer<typeof GridTariff>;

export const Tariffs = z.object({
  grid: GridTariff.optional(),
  /** feed-in compensation, EUR/kWh */
  feedIn: z.object({ price: z.number().min(0).max(1) }).optional(),
});
export type Tariffs = z.infer<typeof Tariffs>;

/** prices the customer sees and may change (ADR-0018); never contains credentials */
export const TariffPrices = z.object({
  grid: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('fixed'), price: z.number(), zones: z.array(PriceZone) }),
      z.object({
        kind: z.literal('dynamic'),
        template: z.string(),
        provider: z.string(),
        /** EUR/kWh on top of the exchange price, if the tariff supports it */
        charges: z.number().optional(),
        tax: z.number().optional(),
      }),
    ])
    .nullable(),
  feedIn: z.number().nullable(),
  /** the viewer may change the prices */
  editable: z.boolean(),
});
export type TariffPrices = z.infer<typeof TariffPrices>;

/** only prices: tariff kind, provider, credentials and time windows stay with the installer */
export const UpdateTariffPrices = z.object({
  grid: z
    .object({
      price: PricePerKWh.optional(),
      /** new prices of the existing zones, same order */
      zonePrices: z.array(PricePerKWh).max(8).optional(),
      charges: PricePerKWh.optional(),
    })
    .optional(),
  feedIn: z.number().min(0).max(1).optional(),
});
export type UpdateTariffPrices = z.infer<typeof UpdateTariffPrices>;

export const SiteConfig = z.object({
  title: z.string().min(1).max(64),
  meters: z.array(Meter).default([]),
  chargers: z.array(Charger).default([]),
  vehicles: z.array(Device).default([]),
  loadpoints: z.array(Loadpoint).default([]),
  /** §14a control box: template device of class hems, or raw */
  hems: z
    .union([TemplateDevice.omit({ name: true }), z.object({ raw: z.record(z.string(), z.unknown()) })])
    .optional(),
  houseConnection: HouseConnection.optional(),
  /** grid and feed-in tariff (ADR-0018); unusual tariff settings stay in extra.tariffs */
  tariffs: Tariffs.optional(),
  /** further evcc circuits kept verbatim (expert settings, admins only) */
  circuits: z.array(z.record(z.string(), z.unknown())).default([]),
  /** write-only: sponsortoken for evcc (ADR-0007) */
  sponsorToken: z.string().max(4096).optional(),
  /** other top-level evcc settings kept verbatim (interval, tariffs, circuits, …) */
  extra: z.record(z.string(), z.unknown()).default({}),
});
export type SiteConfig = z.infer<typeof SiteConfig>;

export function isTemplateDevice(device: object): device is TemplateDevice {
  return 'template' in device;
}

export function isSgReadyRelay(device: object): device is SgReadyRelayDevice {
  return 'sgready' in device;
}

/** free evcc configuration (plugins etc.), only admins may add or change it */
export function isRawDevice(device: object): device is RawDevice {
  return 'raw' in device;
}

/** evcc current for a power: W / (230 V × phases), rounded to 0.1 A */
export const powerToCurrent = (watts: number, phases: 1 | 3) =>
  Math.round((watts / (230 * phases)) * 10) / 10;

export const MASKED = '********';

/**
 * EEBus identity of this evcc (state eebus.status, server/eebus/eebus.go: GetStatus): the FNN
 * control box needs the SKI, or scans the SHIP QR code (contains the pairing secret)
 */
export const EebusInfo = z.object({ ski: z.string().optional(), qr: z.string().optional() });
export type EebusInfo = z.infer<typeof EebusInfo>;

// ---- network of the Pi (ADR-0016) ------------------------------------------------------------

export const WifiNetwork = z.object({ ssid: z.string(), signal: z.number(), secure: z.boolean() });
export type WifiNetwork = z.infer<typeof WifiNetwork>;

/** one network interface of the Pi (LAN eth0 or WLAN wlan0) */
export const NetInterface = z.object({
  device: z.string(),
  /** cable plugged in (LAN) */
  carrier: z.boolean(),
  connected: z.boolean(),
  address: z.string(),
  gateway: z.string(),
  dns: z.array(z.string()),
  mac: z.string(),
  connection: z.string(),
});
export type NetInterface = z.infer<typeof NetInterface>;

export const WifiInterface = NetInterface.extend({
  ssid: z.string(),
  /** 0–100 % */
  signal: z.number().nullable(),
  /** MHz */
  frequency: z.number().nullable(),
});
export type WifiInterface = z.infer<typeof WifiInterface>;

/** WLAN profile NetworkManager knows; the highest priority is tried first */
export const SavedWifi = z.object({
  name: z.string(),
  ssid: z.string(),
  priority: z.number(),
  active: z.boolean(),
});
export type SavedWifi = z.infer<typeof SavedWifi>;

export const NetworkTestStep = z.object({
  key: z.string(),
  /** null: skipped (e.g. tunnel of a Pi not paired yet) */
  ok: z.boolean().nullable(),
  detail: z.string(),
});
export type NetworkTestStep = z.infer<typeof NetworkTestStep>;

export const NetworkTest = z.object({ at: z.string(), ok: z.boolean(), steps: z.array(NetworkTestStep) });
export type NetworkTest = z.infer<typeof NetworkTest>;

/** written by the host (bin/ste-net); the gateway only passes it on */
export const NetworkState = z.object({
  mode: z.enum(['ethernet', 'wifi', 'setup', 'none']),
  ssid: z.string(),
  address: z.string(),
  setupActive: z.boolean(),
  setupSsid: z.string(),
  networks: z.array(WifiNetwork),
  last: z
    .object({ id: z.string(), action: z.string(), ok: z.boolean(), message: z.string(), at: z.string() })
    .nullable(),
  updatedAt: z.string(),
  // details since the second iteration; missing with an older Pi bundle
  ethernet: NetInterface.nullable().optional(),
  wifi: WifiInterface.nullable().optional(),
  saved: z.array(SavedWifi).optional(),
  paired: z.boolean().optional(),
  test: NetworkTest.nullable().optional(),
  setupReason: z.enum(['offline', 'unpaired', 'manual']).nullable().optional(),
  setupUntil: z.string().nullable().optional(),
});
export type NetworkState = z.infer<typeof NetworkState>;

export const NetworkInfo = z.object({
  /** false: the Pi bundle has no network helper (older installation or not a Pi) */
  available: z.boolean(),
  state: NetworkState.optional(),
  /** id of the job still waiting for the host */
  pending: z.string().optional(),
});
export type NetworkInfo = z.infer<typeof NetworkInfo>;

export const WifiConnectRequest = z.object({
  ssid: z
    .string()
    .min(1)
    .refine(
      (s) =>
        new TextEncoder().encode(s).length <= 32 &&
        ![...s].some((c) => c.charCodeAt(0) < 32 || c === '\u007f'),
      'Ungültiger WLAN-Name',
    ),
  /** empty for open networks */
  key: z.string().refine((k) => k === '' || (k.length >= 8 && k.length <= 63 && /^[ -~]+$/.test(k)), {
    message: 'Der WLAN-Schlüssel muss 8 bis 63 Zeichen lang sein',
  }),
});
export type WifiConnectRequest = z.infer<typeof WifiConnectRequest>;

/** a saved WLAN profile by its name (validated again on the host) */
export const SavedWifiRequest = z.object({ name: z.string().min(1).max(64) });
export const SavedWifiOrderRequest = z.object({ names: z.array(z.string().min(1).max(64)).min(1).max(20) });
export const SetupWifiRequest = z.object({ open: z.boolean() });
